/**
 * InlineEditCell — edit a value in the grid without opening the record.
 *
 * THIS IS THE INTERACTION THE PRODUCT LIVES ON. The daily loop is scan the list, update what
 * changed, move on. Requiring a profile round trip per change would make Tidalfin slower than the
 * spreadsheet it replaces, so this is the one component worth getting exactly right.
 *
 * Behaviour (UI Constitution §5.1):
 *   click or Enter   -> edit
 *   Esc              -> cancel, restore the previous value
 *   Enter or blur    -> commit
 *   commit           -> OPTIMISTIC: the new value shows immediately, the request runs behind it
 *   failure          -> the old value is restored AND the error is surfaced, never a silent revert
 *
 * The optimistic update is what makes it feel instant; the rollback is what makes it honest. A
 * cell that keeps showing a value the server rejected is worse than one that never updated.
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'

export type InlineEditType = 'text' | 'longtext' | 'date' | 'select'

export interface InlineEditOption {
  value: string
  label: string
}

export interface InlineEditCellProps {
  value: string | null | undefined
  type?: InlineEditType
  options?: InlineEditOption[]
  placeholder?: string
  /** Extra class on the display element — `next-move` gets the italic treatment. */
  displayClass?: string
  ariaLabel: string
  /** Commit. Rejecting (throwing) rolls the cell back. */
  onCommit: (next: string | null) => Promise<void>
  /** Render the committed value; defaults to the raw string. */
  render?: (value: string | null | undefined) => React.ReactNode
  disabled?: boolean
}

export function InlineEditCell({
  value,
  type = 'text',
  options,
  placeholder = 'Set value',
  displayClass = '',
  ariaLabel,
  onCommit,
  render,
  disabled = false,
}: InlineEditCellProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  //  `shown` is what the user sees. It moves the instant a commit starts (optimistic) and snaps
  //  back if the request fails.
  const [shown, setShown] = useState<string | null | undefined>(value)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>(null)
  const committing = useRef(false)
  //  Esc closes the editor, which can hand the field a blur on the way out. Without this the
  //  blur handler would commit the very edit the user just abandoned.
  const cancelled = useRef(false)

  //  Keep in step when the row is refreshed from the server underneath us — but ONLY when the
  //  prop genuinely changed. Adopting it on every render would snap an accepted edit back to the
  //  stale prop the instant the request resolved, so the value would flicker away and then return
  //  when the parent reloaded — and would be lost outright if the parent never reloaded.
  const lastProp = useRef(value)
  useEffect(() => {
    if (editing || saving) return
    if ((value ?? null) === (lastProp.current ?? null)) return
    lastProp.current = value
    setShown(value)
  }, [value, editing, saving])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      if (inputRef.current instanceof HTMLInputElement) inputRef.current.select()
    }
  }, [editing])

  const begin = useCallback(() => {
    if (disabled) return
    cancelled.current = false
    setDraft(shown ?? '')
    setFailed(false)
    setEditing(true)
  }, [disabled, shown])

  const cancel = useCallback(() => {
    cancelled.current = true
    setEditing(false)
    setDraft(shown ?? '')
  }, [shown])

  const commitValue = useCallback(async (raw: string) => {
    //  blur fires after Enter has already committed; without this guard every Enter sends twice.
    if (committing.current || cancelled.current) return
    const next = raw.trim() === '' ? null : raw.trim()
    const previous = shown ?? null
    setEditing(false)

    if ((next ?? '') === (previous ?? '')) return

    committing.current = true
    setShown(next)
    setSaving(true)
    setFailed(false)
    try {
      await onCommit(next)
    } catch {
      //  Roll back. The caller surfaces the error — this component must not swallow it, and must
      //  not leave a value on screen that the server refused.
      setShown(previous)
      setFailed(true)
      setTimeout(() => setFailed(false), 2600)
    } finally {
      setSaving(false)
      committing.current = false
    }
  }, [onCommit, shown])

  const commit = useCallback(() => commitValue(draft), [commitValue, draft])

  const onKeyDown = (e: KeyboardEvent) => {
    //  Enter commits for every type, `longtext` included. That field is a single-line input, so a
    //  modifier-only commit would leave Enter doing nothing at all — the one key a person will try.
    if (e.key === 'Enter') {
      e.preventDefault()
      void commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  const onDisplayKeyDown = (e: KeyboardEvent) => {
    //  Keyboard parity: Tab reaches the cell, Enter opens it (UI Constitution §10).
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      begin()
    }
  }

  if (editing) {
    if (type === 'select') {
      //  A SELECT WITH NO OPTIONS SAYS SO. Statuses and types are reference data loaded once at
      //  start-up; when that request fails every select in the application renders empty, and
      //  nothing on the control explains it. That turns one failed request into what looks like
      //  several independently broken dropdowns — which is how it gets reported.
      const noOptions = (options ?? []).length === 0
      return (
        <span className="ie-cell">
          <select
            ref={(el) => (inputRef.current = el)}
            className="ie-select"
            value={draft}
            disabled={noOptions}
            aria-label={ariaLabel}
            onChange={(e) => {
              //  Choosing an option IS the commit. Requiring a blur afterwards is how a dropdown
              //  ends up looking like it does nothing.
              setDraft(e.target.value)
              void commitValue(e.target.value)
            }}
            onKeyDown={onKeyDown}
            onBlur={() => void commit()}
          >
            {noOptions ? (
              <option value="">Reference data did not load — reload to try again</option>
            ) : (
              <option value="">— none —</option>
            )}
            {(options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </span>
      )
    }
    return (
      <span className="ie-cell">
        <input
          ref={(el) => (inputRef.current = el)}
          className={`ie-input${type === 'longtext' ? ' wide' : ''}`}
          type={type === 'date' ? 'date' : 'text'}
          value={draft}
          aria-label={ariaLabel}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => void commit()}
        />
      </span>
    )
  }

  const isEmpty = shown === null || shown === undefined || shown === ''
  const content = render ? render(shown) : shown

  return (
    <span className={`ie-cell${saving ? ' saving' : ''}${failed ? ' failed' : ''}`}>
      <span
        className={`ie-view ${displayClass}${isEmpty ? ' placeholder' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        onClick={begin}
        onKeyDown={onDisplayKeyDown}
        title={disabled ? undefined : 'Click to edit'}
      >
        {isEmpty ? placeholder : content}
      </span>
      {saving && <span className="ie-spin" aria-label="Saving" />}
    </span>
  )
}
